<template>
	<section class="table-floating-filter-panel" role="dialog" :aria-label="title">
		<header>
			<h3>{{ title }}</h3>
			<div class="actions">
				<slot name="header-actions" />
				<button
					type="button"
					class="icon-button"
					:aria-label="t('filters.close')"
					:title="t('filters.close')"
					@click="$emit('close')"
				>
					<AppIcon :name="IconName.Close" class="close-icon" />
				</button>
			</div>
		</header>
		<div class="content">
			<slot />
		</div>
	</section>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"

defineProps<{
	title: string
}>()

defineEmits(["close"])

const { t } = useI18n()
</script>

<style scoped>
.table-floating-filter-panel {
	position: absolute;
	top: calc(100% + var(--spacing-xs));
	left: 0;
	z-index: 100;
	width: var(--width-filtering-panel);
	max-height: min(600px, calc(100vh - var(--spacing-xl) * 2));
	display: flex;
	flex-direction: column;
	background-color: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	box-shadow: var(--shadow-lg);
	overflow: hidden;

	header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: var(--spacing-sm) var(--spacing-md) var(--spacing-sm) var(--spacing-xl);
		border-bottom: var(--border-width-thin) solid var(--neutral-200);

		h3 {
			margin: 0;
			color: var(--neutral-900);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-semibold);
			text-transform: uppercase;
			letter-spacing: 0.02em;
		}

		.actions {
			display: flex;
			align-items: center;
			gap: var(--spacing-xxs);
		}
	}

	.icon-button {
		width: var(--size-icon-xl);
		height: var(--size-icon-xl);
		border: none;
		background: transparent;
		color: var(--neutral-600);
		cursor: pointer;
		padding: var(--spacing-xxs);
		display: inline-flex;
		align-items: center;
		justify-content: center;

		&:hover {
			color: var(--neutral-900);
		}

		.close-icon {
			width: var(--size-icon-sm);
			height: var(--size-icon-sm);
			stroke: currentColor;
			fill: none;
			stroke-width: 2;
			stroke-linecap: round;
		}
	}

	.content {
		flex-grow: 1;
		overflow-y: auto;
		padding: var(--spacing-md) var(--spacing-xl);
	}
}
</style>
